import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowLeft, Lightning, Users, Clock, Trophy, ChartBar, MagnifyingGlass, SortAscending, SortDescending, Medal, Warning, CheckCircle, Timer } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";

const URGENCY_COLORS = { critical: "#dc2626", urgent: "#ea580c", moderate: "#d97706", minor: "#2563eb" };
const URGENCY_LABELS = { critical: "Critical", urgent: "Urgent", moderate: "Moderate", minor: "Minor" };

function fmtHours(h) {
  if (h === null || h === undefined) return "—";
  if (h < 1) return `${Math.round(h * 60)}m`;
  return `${h}h`;
}

function getRankColor(i) {
  if (i === 0) return "bg-amber-400 text-amber-950";
  if (i === 1) return "bg-gray-300 text-gray-800";
  if (i === 2) return "bg-orange-300 text-orange-900";
  return "bg-muted text-muted-foreground";
}

function getSlaColor(rate) {
  if (rate === null || rate === undefined) return "text-muted-foreground";
  if (rate >= 90) return "text-emerald-600 dark:text-emerald-400";
  if (rate >= 70) return "text-amber-600 dark:text-amber-400";
  return "text-red-600 dark:text-red-400";
}

export default function SupplierResponsePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("avg_ack");
  const [sortDir, setSortDir] = useState("asc");

  useEffect(() => {
    API.get("/emergency-jobs/supplier-response")
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const suppliers = useMemo(() => {
    if (!data?.suppliers) return [];
    let list = [...data.suppliers];
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(v => v.vendor_name?.toLowerCase().includes(s));
    }
    list.sort((a, b) => {
      let va, vb;
      switch (sortBy) {
        case "avg_ack": va = a.avg_ack_hours; vb = b.avg_ack_hours; break;
        case "sla": va = a.sla_compliance_rate; vb = b.sla_compliance_rate; break;
        case "ack_rate": va = a.acknowledge_rate; vb = b.acknowledge_rate; break;
        case "total": va = a.total_jobs; vb = b.total_jobs; break;
        case "name": return sortDir === "asc" ? (a.vendor_name || "").localeCompare(b.vendor_name || "") : (b.vendor_name || "").localeCompare(a.vendor_name || "");
        default: va = a.avg_ack_hours; vb = b.avg_ack_hours;
      }
      if (va === null || va === undefined) return 1;
      if (vb === null || vb === undefined) return -1;
      return sortDir === "asc" ? va - vb : vb - va;
    });
    return list;
  }, [data, search, sortBy, sortDir]);

  if (loading) return <div className="text-center py-20 text-muted-foreground">Loading supplier response data...</div>;
  if (!data || data.suppliers?.length === 0) return (
    <div className="text-center py-20">
      <Users size={36} className="mx-auto text-muted-foreground/40 mb-3" />
      <p className="text-sm text-muted-foreground mb-3">No supplier response data yet</p>
      <p className="text-xs text-muted-foreground mb-4">Assign suppliers to emergency jobs to start tracking response times</p>
      <Link to="/emergency-jobs"><Button variant="outline" size="sm">Go to Emergency Jobs</Button></Link>
    </div>
  );

  const { overall, urgency_breakdown } = data;

  // Chart data — avg ack time per supplier (top 12)
  const chartData = suppliers.slice(0, 12).filter(s => s.avg_ack_hours !== null).map(s => ({
    name: s.vendor_name.length > 18 ? s.vendor_name.slice(0, 16) + "..." : s.vendor_name,
    avg: s.avg_ack_hours,
    sla: s.sla_compliance_rate || 0,
    full_name: s.vendor_name,
  }));

  return (
    <div data-testid="supplier-response-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-5">
        <div className="flex items-center gap-3">
          <Link to="/emergency-jobs/analytics"><ArrowLeft size={18} className="text-muted-foreground hover:text-foreground" /></Link>
          <div className="h-9 w-9 rounded-lg bg-indigo-600 flex items-center justify-center">
            <Timer size={18} className="text-white" weight="fill" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight">Supplier Response Dashboard</h1>
            <p className="text-xs text-muted-foreground">{overall.total_suppliers} suppliers &middot; {overall.total_jobs} emergency jobs tracked</p>
          </div>
        </div>
        <Link to="/emergency-jobs">
          <Button variant="outline" size="sm"><Lightning size={14} className="mr-1 text-red-500" /> Emergency Jobs</Button>
        </Link>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="response-kpi-cards">
        <div className="border border-border rounded-md p-3 bg-card" data-testid="kpi-avg-response">
          <Clock size={18} className="text-indigo-500 mb-1.5" weight="duotone" />
          <p className="text-xl font-bold font-[Chivo]">{fmtHours(overall.overall_avg_ack_hours)}</p>
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider mt-0.5">Avg Response Time</p>
        </div>
        <div className="border border-border rounded-md p-3 bg-card" data-testid="kpi-ack-rate">
          <CheckCircle size={18} className="text-emerald-500 mb-1.5" weight="duotone" />
          <p className="text-xl font-bold font-[Chivo]">{overall.overall_ack_rate}%</p>
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider mt-0.5">Acknowledge Rate</p>
        </div>
        <div className="border border-border rounded-md p-3 bg-card" data-testid="kpi-fastest">
          <Trophy size={18} className="text-amber-500 mb-1.5" weight="duotone" />
          <p className="text-lg font-bold font-[Chivo] truncate" title={overall.fastest_supplier}>{overall.fastest_supplier || "—"}</p>
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider mt-0.5">Fastest Responder {overall.fastest_avg_hours != null && `(${fmtHours(overall.fastest_avg_hours)})`}</p>
        </div>
        <div className="border border-border rounded-md p-3 bg-card" data-testid="kpi-total-suppliers">
          <Users size={18} className="text-blue-500 mb-1.5" weight="duotone" />
          <p className="text-xl font-bold font-[Chivo]">{overall.total_suppliers}</p>
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider mt-0.5">Active Suppliers</p>
        </div>
      </div>

      {/* Urgency Response Breakdown */}
      <div className="border border-border rounded-lg p-5 mb-6" data-testid="urgency-response-breakdown">
        <h2 className="text-base font-semibold font-[Chivo] mb-4 flex items-center gap-2"><Lightning size={16} className="text-red-500" /> Response by Urgency Level</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(URGENCY_LABELS).map(([key, label]) => {
            const urg = urgency_breakdown?.[key] || {};
            const ackRate = urg.total ? Math.round((urg.acknowledged / urg.total) * 100) : 0;
            const slaRate = urg.total ? Math.round(((urg.sla_met || 0) / urg.total) * 100) : 0;
            return (
              <div key={key} className="border border-border rounded-md p-4" data-testid={`urg-response-${key}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-bold text-white" style={{ background: URGENCY_COLORS[key] }}>{label}</span>
                  <span className="text-[10px] text-muted-foreground">{urg.target_hours}h target</span>
                </div>
                <p className="text-xl font-bold font-[Chivo]">{fmtHours(urg.avg_ack_hours)}</p>
                <p className="text-[9px] text-muted-foreground uppercase tracking-wider mb-2">Avg Acknowledge</p>
                <div className="space-y-1.5 text-[10px]">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Ack Rate</span>
                    <span className="font-semibold">{ackRate}% ({urg.acknowledged || 0}/{urg.total || 0})</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">SLA Met</span>
                    <span className={`font-semibold ${getSlaColor(slaRate)}`}>{slaRate}%</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Response Time Chart */}
      {chartData.length > 0 && (
        <div className="border border-border rounded-lg p-5 mb-6" data-testid="response-time-chart">
          <h2 className="text-sm font-semibold font-[Chivo] mb-4 flex items-center gap-2"><ChartBar size={14} /> Avg Acknowledge Time by Supplier</h2>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10 }} unit="h" />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={130} />
              <Tooltip content={({ active, payload }) => {
                if (!active || !payload?.[0]) return null;
                const d = payload[0].payload;
                return (
                  <div className="bg-popover border border-border rounded-md p-2.5 shadow-md text-xs">
                    <p className="font-semibold mb-1">{d.full_name}</p>
                    <p>Avg Response: <span className="font-bold">{d.avg}h</span></p>
                    <p>SLA Compliance: <span className="font-bold">{d.sla}%</span></p>
                  </div>
                );
              }} />
              <Bar dataKey="avg" radius={[0, 4, 4, 0]} maxBarSize={24}>
                {chartData.map((entry, i) => (
                  <Cell key={i} fill={entry.sla >= 80 ? "#059669" : entry.sla >= 50 ? "#d97706" : "#dc2626"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="flex items-center gap-4 mt-2 justify-center">
            <div className="flex items-center gap-1.5 text-[10px]"><div className="w-2.5 h-2.5 rounded-sm bg-emerald-600" /> SLA 80%+</div>
            <div className="flex items-center gap-1.5 text-[10px]"><div className="w-2.5 h-2.5 rounded-sm bg-amber-600" /> SLA 50-79%</div>
            <div className="flex items-center gap-1.5 text-[10px]"><div className="w-2.5 h-2.5 rounded-sm bg-red-600" /> SLA &lt;50%</div>
          </div>
        </div>
      )}

      {/* Supplier Leaderboard Table */}
      <div className="border border-border rounded-lg overflow-hidden" data-testid="supplier-leaderboard">
        <div className="px-4 py-3 bg-muted/30 border-b border-border flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <h2 className="text-sm font-semibold font-[Chivo] flex items-center gap-2"><Medal size={16} className="text-amber-500" /> Supplier Response Leaderboard</h2>
          <div className="flex items-center gap-2">
            <div className="relative">
              <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search supplier..." className="pl-7 h-7 text-xs w-44" data-testid="supplier-search" />
            </div>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[140px] h-7 text-xs" data-testid="sort-by"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="avg_ack">Avg Response</SelectItem>
                <SelectItem value="sla">SLA Compliance</SelectItem>
                <SelectItem value="ack_rate">Ack Rate</SelectItem>
                <SelectItem value="total">Total Jobs</SelectItem>
                <SelectItem value="name">Name</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setSortDir(d => d === "asc" ? "desc" : "asc")} data-testid="sort-dir-toggle">
              {sortDir === "asc" ? <SortAscending size={14} /> : <SortDescending size={14} />}
            </Button>
          </div>
        </div>

        {suppliers.length === 0 ? (
          <p className="px-4 py-8 text-xs text-muted-foreground text-center">No suppliers match your search</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="supplier-table">
              <thead>
                <tr className="border-b border-border bg-muted/20">
                  <th className="px-3 py-2.5 text-left font-semibold text-muted-foreground w-8">#</th>
                  <th className="px-3 py-2.5 text-left font-semibold text-muted-foreground">Supplier</th>
                  <th className="px-3 py-2.5 text-center font-semibold text-muted-foreground">Jobs</th>
                  <th className="px-3 py-2.5 text-center font-semibold text-muted-foreground">Ack'd</th>
                  <th className="px-3 py-2.5 text-center font-semibold text-muted-foreground">Ack Rate</th>
                  <th className="px-3 py-2.5 text-center font-semibold text-muted-foreground">Avg Response</th>
                  <th className="px-3 py-2.5 text-center font-semibold text-muted-foreground">Min / Max</th>
                  <th className="px-3 py-2.5 text-center font-semibold text-muted-foreground">SLA Compliance</th>
                  <th className="px-3 py-2.5 text-center font-semibold text-muted-foreground">Completed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {suppliers.map((s, i) => (
                  <tr key={s.vendor_id} className="hover:bg-muted/10 transition-colors" data-testid={`supplier-row-${i}`}>
                    <td className="px-3 py-3">
                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold ${getRankColor(i)}`}>{i + 1}</span>
                    </td>
                    <td className="px-3 py-3">
                      <p className="font-semibold text-sm">{s.vendor_name}</p>
                      <div className="flex gap-1.5 mt-1">
                        {Object.entries(s.by_urgency || {}).map(([urg, d]) => (
                          <span key={urg} className="text-[9px] px-1.5 py-0.5 rounded font-medium" style={{ background: URGENCY_COLORS[urg] + "18", color: URGENCY_COLORS[urg] }}>
                            {URGENCY_LABELS[urg]}: {d.total}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-center font-bold">{s.total_jobs}</td>
                    <td className="px-3 py-3 text-center">{s.acknowledged}</td>
                    <td className="px-3 py-3 text-center">
                      <span className={`font-bold ${s.acknowledge_rate >= 80 ? "text-emerald-600" : s.acknowledge_rate >= 50 ? "text-amber-600" : "text-red-600"}`}>
                        {s.acknowledge_rate}%
                      </span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className="font-bold font-mono">{fmtHours(s.avg_ack_hours)}</span>
                    </td>
                    <td className="px-3 py-3 text-center text-muted-foreground">
                      {s.min_ack_hours != null ? `${fmtHours(s.min_ack_hours)} / ${fmtHours(s.max_ack_hours)}` : "—"}
                    </td>
                    <td className="px-3 py-3 text-center">
                      {s.sla_compliance_rate != null ? (
                        <div className="flex flex-col items-center gap-1">
                          <span className={`font-bold ${getSlaColor(s.sla_compliance_rate)}`}>{s.sla_compliance_rate}%</span>
                          <Progress value={s.sla_compliance_rate} className="h-1 w-14" />
                          <span className="text-[9px] text-muted-foreground">{s.sla_met} met / {s.sla_breached} breached</span>
                        </div>
                      ) : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="px-3 py-3 text-center">{s.completed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
